import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaClock, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { fetchWithAuth } from '../services/api';

const EmailVerificationDialog = ({ userEmail, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleClose = () => {
    onClose();
    // Redirect to login page
    const currentLang = window.location.pathname.split('/')[1] || 'mk';
    navigate(`/${currentLang}/login`);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendError('');
    setResendMessage('');
    setResendSuccess(false);

    try {
      const data = await fetchWithAuth(`/auth/resend-verification?email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
      });

      if (data.success) {
        setResendSuccess(true);
        setResendMessage(t('emailVerification.resendSuccess'));
      } else {
        setResendError(data.message || t('emailVerification.resendError'));
        
        // Extract countdown time from error message if rate limited
        const waitTimeMatch = data.message?.match(/(\d+)\s+minutes?/);
        if (waitTimeMatch) {
          const minutes = parseInt(waitTimeMatch[1]);
          setCountdown(minutes * 60); // Convert to seconds
        }
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setResendError(t('emailVerification.resendError'));
    } finally {
      setIsResending(false);
    }
  };

  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${remainingSeconds}s`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Close X Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          aria-label="Close"
        >
          <FaTimes className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <FaEnvelope className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('emailVerification.title')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('emailVerification.description')}
          </p>
        </div>

        {/* Email Display */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <FaEnvelope className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-900">{userEmail}</span>
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-4">
            {t('emailVerification.instructions')}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
            <div className="flex items-start">
              <FaClock className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-xs text-blue-800">
                {t('emailVerification.expirationNote')}
              </p>
            </div>
          </div>
          
          {/* Spam/Junk Folder Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start">
              <svg className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-xs font-medium text-yellow-800 mb-1">
                  {t('emailVerification.spamWarningTitle')}
                </p>
                <p className="text-xs text-yellow-700">
                  {t('emailVerification.spamWarningText')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {resendSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <FaCheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <p className="text-sm text-green-800">{resendMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {resendError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{resendError}</p>
          </div>
        )}

        {/* Resend Button */}
        <div className="mb-6">
          <button
            onClick={handleResendEmail}
            disabled={isResending || countdown > 0}
            className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${
              isResending || countdown > 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            }`}
          >
            {isResending ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('emailVerification.sending')}
              </span>
            ) : countdown > 0 ? (
              `${t('emailVerification.resendIn')} ${formatCountdown(countdown)}`
            ) : (
              t('emailVerification.resendButton')
            )}
          </button>
        </div>

        {/* Close Button */}
        <div className="text-center">
          <button
            onClick={handleClose}
            className="text-sm text-red-500 hover:text-red-700 transition-colors duration-200"
          >
            {t('emailVerification.closeButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationDialog; 